import React from 'react';
import { logger } from 'shared/lib/logger';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * App-level error boundary. Catches render-time exceptions thrown anywhere
 * below it and renders a fallback so a single bad component cannot bring the
 * entire onboarding flow down. Logs the error to the console; swap this for
 * Sentry / Datadog / your monitoring of choice in production.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error('[HAAT Partner Connect] render error', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full card p-6 text-center">
          <h1 className="text-lg font-bold text-gray-900">Something went wrong</h1>
          <p className="mt-2 text-sm text-gray-600">
            An unexpected error occurred. Please refresh the page or try again.
          </p>
          {this.state.error?.message && (
            <pre className="mt-3 text-[11px] text-start bg-gray-50 border border-gray-100 rounded-lg p-2 overflow-auto text-gray-500">
              {this.state.error.message}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleReset}
            className="btn-haat inline-flex items-center gap-2 mt-4"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
